import React from 'react';
import { KANBAN_COLUMNS } from '../types';
import { GestaoKanbanCard } from './GestaoKanbanCard';
import type { TaskListItem } from '../../shared/types/task';

interface GestaoKanbanBoardProps {
  tasks: TaskListItem[];
  onTaskClick: (taskId: string) => void;
  isLoading: boolean;
  onOpenExecution?: (executionId: string) => void;
  compactMode?: boolean;
}

export const GestaoKanbanBoard: React.FC<GestaoKanbanBoardProps> = ({
  tasks,
  onTaskClick,
  isLoading,
  onOpenExecution,
  compactMode = false,
}) => {
  const toFiniteNumber = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const grouped = KANBAN_COLUMNS.map((col) => ({
    ...col,
    tasks: tasks.filter((t) => col.statuses.includes(t.status)),
  }));

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center text-slate-400 dark:text-slate-500">
        <div className="animate-pulse text-sm">Carregando tarefas...</div>
      </div>
    );
  }

  return (
    <div className={`enterprise-kanban-shell flex-1 overflow-x-auto ${compactMode ? 'gap-3 p-3' : 'gap-4 p-4'}`}>
      {grouped.map((col) => {
        const wip = col.tasks.filter((task) => ['pending', 'in_progress'].includes(task.status)).length;
        const overdue = col.tasks.filter((task) => task.isOverdue).length;
        const wipLimit = col.tasks
          .map((task) => (
            toFiniteNumber(task.metadata?.wipLimit) ??
            toFiniteNumber(task.metadata?.wip_limit) ??
            toFiniteNumber(task.metadata?.stageWipLimit) ??
            toFiniteNumber(task.metadata?.stage_wip_limit)
          ))
          .find((value): value is number => value !== null);
        const isWipExceeded = wipLimit !== undefined && wipLimit !== null && wip > wipLimit;

        return (
          <div
            key={col.id}
            className={`enterprise-kanban-column flex flex-col ${isWipExceeded ? 'column-wip-warning border-[var(--wip-warning)]' : ''} ${compactMode ? 'min-w-[200px]' : 'min-w-[220px]'} flex-1`}
          >
            <div className="enterprise-kanban-header border-b border-enterprise-border-strong px-2.5 py-1.5">
              <div className={`font-mono text-[11px] uppercase tracking-[0.12em] enterprise-text-primary ${isWipExceeded ? 'font-semibold' : 'font-medium'}`}>
                {col.label.toUpperCase()} | {col.tasks.length}
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] enterprise-text-secondary">
                WIP: {wip} | ATRASO: {overdue}
              </div>
              {isWipExceeded ? (
                <div className="risk-alert mt-1 font-mono text-[10px] font-medium uppercase tracking-[0.12em]">
                  WIP_EXCEDIDO
                </div>
              ) : null}
            </div>

            <div className={`flex-1 overflow-y-auto ${compactMode ? 'space-y-1 p-1.5' : 'space-y-1.5 p-2'}`}>
              {col.tasks.length === 0 ? (
                <p className="py-6 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400 dark:text-slate-600">
                  Nenhuma tarefa
                </p>
              ) : (
                col.tasks.map((task) => (
                  <GestaoKanbanCard
                    key={task.id}
                    task={task}
                    onClick={() => onTaskClick(task.id)}
                    onOpenExecution={onOpenExecution}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
